import { jwtDecode } from 'jwt-decode';
import UUID from 'uuid-js';
import ErrorCodes from '../consts/ErrorCodes';
import Urls from '../consts/Urls';
import useStore from '../store/store';
import api from './api';

export default class WsClient {
    /** @public */
    constructor(aUrl) {
        this.theUrl = aUrl;

        this.theIsConnected = false;
        this.theClient = null;
        this.theIsFullyInitialized = false;

        this.theRetryCount = 0;
        this.theRetryTimeout = null;

        // Create maps for storing callbacks and a request queue.
        this.theRequestMap = {};
        this.theRequestQueue = [];

        // Create map for subscriptions.
        this.theSubscriptionMap = {};
        this.theSubscriptionCallbacks = [];

        // Reconnect callbacks.
        this.theReconnectCallbacks = [];
        this.theShouldRunReconnectCallbacks = false;

        // Reload callbacks.
        this.theReloadCallbacks = [];
    }

    clearRetryTimeout() {
        if (this.theRetryTimeout) {
            clearTimeout(this.theRetryTimeout);
            this.theRetryTimeout = null;
        }
    }

    /** @private */
    closeClient(aShouldSaveQueue = false) {
        if (this.theClient) {
            console.info(`Closing ${this.theUrl} WebSocket.`);

            if (aShouldSaveQueue) {
                this.saveCurrentRequests();
            } else {
                this.theIsFullyInitialized = false;
            }

            this.theIsConnected = false;

            // Unbind the callbacks.
            this.theClient.onopen = null;
            this.theClient.onclose = null;
            this.theClient.onmessage = null;
            this.theClient.onerror = null;

            // Close and delete the client.
            this.theClient.close();
            this.theClient = null;
        }
    }

    /** @public */
    clearInternalState() {
        // Clean request map and queue
        this.theRequestMap = {};
        this.theRequestQueue = [];

        // Clean subscriptions map
        this.theSubscriptionMap = {};
    }

    /** @private */
    connect() {
        const setFormError = useStore.getState().setFormError;

        if (this.theIsConnected) {
            throw new Error(`Already connected to ${this.theUrl}.`);
        }

        this.clearRetryTimeout();
        this.theIsConnected = false;
        this.closeClient();

        // Create WebSocket instance.
        try {
            this.theClient = new WebSocket(this.theUrl);
        } catch (error) {
            console.error(error);
            setFormError(ErrorCodes.failed_to_create_websocket);
        }
        console.info(`Connecting to ${this.theUrl} via WebSocket.`);

        // Bind the callbacks.
        this.theClient.onopen = this.onOpen;
        this.theClient.onclose = this.onClose;
        this.theClient.onmessage = this.onMessage;
        this.theClient.onerror = this.onError;
    }

    /** @private */
    onOpen = async () => {
        console.info(`Connected to ${this.theUrl} via WebSocket.`);

        if (this.theIsFullyInitialized === false) {
            this.theIsFullyInitialized = true;
        }

        // If reconnected.
        if (this.theRetryCount > 0 || this.theShouldRunReconnectCallbacks) {
            // Sync profile and dictionaries.
            for (let i = 0; i < this.theReconnectCallbacks.length; i++) {
                this.theReconnectCallbacks[i]();
            }
            this.theShouldRunReconnectCallbacks = false;

            // Inform components that they should be reloaded when focused.
            for (let i = 0; i < this.theReloadCallbacks.length; i++) {
                this.theReloadCallbacks[i]();
            }

            // Restart subscriptions.
            for (let i = 0; i < this.theSubscriptionCallbacks.length; i++) {
                this.theSubscriptionCallbacks[i]();
            }
        }

        this.theRetryCount = 0;
        this.theIsConnected = true;

        if (this.theRequestQueue.length > 0) {
            console.info('Running queued requests!');
            const token = await getToken();
            for (const { config, callback, params } of this.theRequestQueue) {
                // Supply access token (it might have changed).
                config.authorization = `Bearer ${token}`;
                this.send(config, callback, params);
            }

            // Clear the queue.
            this.theRequestQueue.length = 0;
        }
    };

    /** @private */
    onClose = (e) => {
        if (this.theIsConnected) {
            this.theIsConnected = false;

            console.warn(
                `Lost WebSocket connection with ${this.theUrl}. Reconnecting...`
            );
            this.theRetryCount++;
            this.connect();
        }
    };

    /** @private */
    onError = (message, url, line, column, e) => {
        const setFormError = useStore.getState().setFormError;

        this.theIsConnected = false;

        // Put all failed requests into the queue.
        this.saveCurrentRequests();

        // Try to reconnect.
        this.theRetryCount++;
        let time;
        if (this.theRetryCount < 5) {
            time = this.theRetryCount * 1000;
        } else if (this.theRetryCount < 10) {
            time = this.theRetryCount * 2000;
        } else if (this.theRetryCount < 20) {
            time = this.theRetryCount * 3000;
        } else {
            time = 60000;
        }

        console.warn(
            `Error during communication with ${this.theUrl} via WebSocket. Will retry in ${
                time / 1000
            } second(s)...`
        );

        if (this.theRetryCount > 1)
            setFormError(ErrorCodes.rpc_communication_error);

        this.theRetryTimeout = setTimeout(this.connect.bind(this), time);
    };

    /** @private */
    onMessage = (e) => {
        try {
            // Parse received data.
            const data = JSON.parse(e.data);
            const { body, id, stream } = data;

            // Detect if error.
            if (body.errCode && body.errCode === ErrorCodes.invalid_token) {
                console.warn(
                    `Received error: '${body.errCode}' from ${this.theUrl}!`
                );
                this.refreshToken();
            } else if (
                body.errCode &&
                body.errCode === ErrorCodes.auth_failed
            ) {
                console.warn(
                    `Received error: '${body.errCode}' from ${this.theUrl}!`
                );
                // Close the WebSocket client.
                this.closeClient();

                // Remove stored tokens and data.
                cleanUp();

                // Navigate to AuthScreen.
                // navigate('AuthWelcome')
            } else {
                if (id) {
                    // Read request details from map.
                    const { callback, params } = this.theRequestMap[id];

                    if (callback) {
                        // Call the provided callback.
                        callback(body, params);

                        // Clean the map.
                        delete this.theRequestMap[id];
                    }
                } else if (
                    stream &&
                    typeof stream === 'string' &&
                    this.theSubscriptionMap[stream]
                ) {
                    // Read request details from map.
                    const { callback } = this.theSubscriptionMap[stream];

                    if (callback) {
                        // Call the provided callback.
                        callback(body);
                    }
                }
            }
        } catch (error) {
            console.warn(`Failed to handle incoming data: ${e.data}`, error);
        }
    };

    /** @public */
    send = (config, callback, params = {}) => {
        if (this.theIsConnected) {
            if (params.subscription) {
                // Check if cancelling subscription.
                if (params.cancel) {
                    delete this.theSubscriptionMap[params.subscription];
                } else {
                    // Remember the callback in subscriptionMap. Callback is called in onMessage.
                    this.theSubscriptionMap[params.subscription] = {
                        config,
                        callback,
                    };
                }

                // Clear params.
                delete params.subscription;
                delete params.cancel;

                // Remember the callback in requestMap too, as the subscription related
                //  requests return success or errCode.
                // Callback is called in onMessage.
                this.theRequestMap[config.id] = { config, callback, params };
            } else {
                // Remember the callback in requestMap. Callback is called in onMessage.
                this.theRequestMap[config.id] = { config, callback, params };
            }

            // Send the request.
            this.theClient.send(JSON.stringify(config));
        } else {
            // If WebSocket is not connected, queue the request.
            this.theRequestQueue.push({
                config,
                callback,
                params,
            });
        }
    };

    /** @private */
    saveCurrentRequests = () => {
        console.warn('Moving requests from map to queue.');

        const queuedEndpoints = new Set(
            this.theRequestQueue.map((req) => req.config.endpoint)
        );

        for (const key in this.theRequestMap) {
            const request = this.theRequestMap[key];
            const { callback, params, config } = request;
            // Skip dictionary changes, user profile update and subscription request as they're handled separately.
            if (
                config &&
                config.endpoint !== '/dictionary/get-changes' &&
                config.endpoint !== '/user/get-profile' &&
                config.endpoint.indexOf('subscribe') === -1
            ) {
                if (!queuedEndpoints.has(config.endpoint)) {
                    this.theRequestQueue.push({
                        callback,
                        params,
                        config: { ...config, id: UUID.create().toString() },
                    });
                    queuedEndpoints.add(config.endpoint);
                }
            }
        }

        // Clean the request map.
        this.theRequestMap = {};
    };

    /** @public */
    isConnected = () => {
        return this.theIsConnected;
    };

    /** @public */
    isFullyInitialized = () => {
        return this.theIsFullyInitialized;
    };

    /** @public */
    addReconnectCallback = (callbacks = []) => {
        this.theReconnectCallbacks = callbacks;
    };

    /** @public */
    addReloadCallbacks = (callbacks = []) => {
        this.theReloadCallbacks = callbacks;
    };

    /** @public */
    addSubscriptionCallbacks = (callbacks = []) => {
        this.theSubscriptionCallbacks = callbacks;
    };

    /** @public */
    setShouldReconnectCallbacks = (value) => {
        this.theShouldRunReconnectCallbacks = value;
    };

    /** @private */
    refreshToken = async () => {
        // Since no request will succeed till token is refreshed
        // we're closing the client.
        this.closeClient(true);
        this.setShouldReconnectCallbacks(true);

        const refreshToken = localStorage.getItem('refreshToken');

        if (refreshToken) {
            // Check if refresh token is still valid.
            // Decode refreshToken.
            const decodedRefreshToken = jwtDecode(refreshToken);

            // If refresh token is not expired then refresh main token.
            const secondsSinceEpoch = Math.floor(Date.now() / 1000);
            if (decodedRefreshToken.exp < secondsSinceEpoch) {
                // Remove stored tokens and data.
                cleanUp();
            }

            // Update Axios header to use refresh token instead.
            api.defaults.headers.common.Authorization = `Bearer ${refreshToken}`;

            console.info('Fetching new token...');
            return api
                .post('/security/refresh-access-token')
                .then((refreshTokenResponse) => {
                    const { success, accessToken, errCode } =
                        refreshTokenResponse.data;

                    if (success && accessToken && !errCode) {
                        console.info('Received new token.');

                        // Update Axios header to use accessToken instead of refresh token.
                        api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

                        // Store new token.
                        localStorage.setItem('accessToken', accessToken);

                        // Restart the connection.
                        this.connect();
                    } else {
                        console.error('Failed to refresh access token.');
                        cleanUp();
                    }
                })
                .catch(() => {
                    console.error('Failed to refresh access token.');
                    cleanUp();
                });
        } else {
            console.error('Refresh token not available!');
            cleanUp();
        }
    };
}

export const ws = new WsClient(Urls.baseWsUrl);
export const wsAdmin = new WsClient(Urls.adminWsUrl);

// This is a wrapper around ws.send().
export const sendRequest = async (config, callback, params = {}) => {
    // Read token before each request since it could change (due to refresh).
    const token = await getToken();

    // Expand the config with general params.]
    config.authorization = `Bearer ${token}`;
    config.contentType = 'application/json';
    config.id = UUID.create().toString();

    // Pass the params to WebSocket client and send the request.
    ws.send(config, callback, params);
};

// This is a wrapper around wsAdmin.send().
export const sendAdminRequest = async (config, callback, params = {}) => {
    // Read token before each request since it could change (due to refresh).
    const token = await getToken();

    // Expand the config with general params.]
    config.authorization = `Bearer ${token}`;
    config.contentType = 'application/json';
    config.id = UUID.create().toString();

    // Pass the params to WebSocket client and send the request.
    wsAdmin.send(config, callback, params);
};

// Helper function to retrive token.
const getToken = async () => {
    const token = localStorage.getItem('accessToken');
    return token;
};

const cleanUp = () => {
    // Remove the headers
    api.defaults.headers.common.Authorization = null;

    // Clean storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('phone');
};
